import { useCallback, useMemo } from 'react';
import { Filters } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { DATE_RANGES } from '../../constants/activityConfig.js';

/** Presets shown in the Time-range chip — "All" is the empty/default state. */
const RANGE_OPTIONS = DATE_RANGES.filter((r) => r.value !== 'all');

/**
 * Audit Ledger toolbar — Filters search + Time-range chip (24h / 7d / 30d).
 * Clearing the chip restores All (no time cutoff).
 */
function AuditLedgerFilters({ range, onRangeChange, search, onSearchChange }) {
  const { t } = useTranslation('work-queues-app');
  const searchLabel = t('activity.searchPlaceholder');

  const handleSearchChange = useCallback(
    (e) => onSearchChange(e?.target ? e.target.value : e),
    [onSearchChange],
  );

  const handleSearchClear = useCallback(() => onSearchChange(''), [onSearchChange]);

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: handleSearchChange,
      onClear: handleSearchClear,
      placeholder: searchLabel,
      'aria-label': searchLabel,
    }),
    [search, handleSearchChange, handleSearchClear, searchLabel],
  );

  const rangeOptions = useMemo(
    () => RANGE_OPTIONS.map((r) => ({ value: r.value, label: t(r.labelKey) })),
    [t],
  );

  const selectedRangeLabel = rangeOptions.find((o) => o.value === range)?.label;

  const handleRangeChange = useCallback(
    (next) => onRangeChange(next[0] ?? 'all'),
    [onRangeChange],
  );

  const filtersConfig = useMemo(
    () => [
      {
        id: 'range',
        label: selectedRangeLabel ?? t('activity.dateRange'),
        multiple: false,
        value: range && range !== 'all' ? [range] : [],
        onChange: handleRangeChange,
        options: rangeOptions,
      },
    ],
    [range, handleRangeChange, rangeOptions, selectedRangeLabel, t],
  );

  const filtersI18n = useMemo(
    () => ({
      clearFilters: t('filters.clearAll'),
      allFilters: t('filters.allFilters'),
    }),
    [t],
  );

  const handleClearAll = useCallback(() => {
    onSearchChange('');
    onRangeChange('all');
  }, [onSearchChange, onRangeChange]);

  return (
    <Filters
      searchProps={searchProps}
      filters={filtersConfig}
      showAllFilters={false}
      maxFilters={1}
      onClearAll={handleClearAll}
      i18n={filtersI18n}
    />
  );
}

export default AuditLedgerFilters;
